import {
    type OfferType,
    type IPenciledInputs,
    type RollToOption,
    type RollToOptions,
    type TermOverrides
} from '@makemydeal/dr-platform-types';
import {
    isImplausibleMoneyFactor,
    refusesImplausibleMoneyFactor,
    withoutImplausibleBuyRate,
    withoutImplausibleRates
} from './rateUtils';

const declaredRateTypeForTerm = (
    penciledInputs: IPenciledInputs,
    offerType: OfferType,
    termMonth?: number | null
): string | undefined => {
    const inputs = penciledInputs?.byOfferType?.[offerType];
    return inputs?.overridesByTerm?.[termMonth!]?.rateType ?? inputs?.rateTypeOverride;
};

const carriedTermOverride = (
    penciledInputs: IPenciledInputs,
    offerType: OfferType,
    termMonth: number | null | undefined,
    effectiveRateType: string | undefined,
    writeSellRate: boolean
): TermOverrides => {
    const carried: TermOverrides = penciledInputs?.byOfferType?.[offerType]?.overridesByTerm?.[termMonth!] ?? {};
    const cleaned = withoutImplausibleBuyRate(withoutImplausibleRates(carried, carried.rateType), effectiveRateType);
    return writeSellRate && carried.rateType ? { ...cleaned, rateType: carried.rateType } : cleaned;
};

const isRefusedRate = (
    site: string,
    rateType: string | undefined,
    option: RollToOption | undefined,
    offerType: OfferType | undefined,
    logRefusals: boolean
): boolean => {
    if (!logRefusals) return isImplausibleMoneyFactor(rateType, option?.value);
    return refusesImplausibleMoneyFactor(site, rateType, option?.value, offerType);
};

const isWritableRate = (
    site: string,
    rateType: string | undefined,
    option: RollToOption | undefined,
    offerType: OfferType | undefined,
    logRefusals: boolean
): boolean => Boolean(option?.adjusted) && !isRefusedRate(site, rateType, option, offerType, logRefusals);

export const buildRollToPenciledInputsState = (
    penciledInputs: IPenciledInputs,
    overrides: RollToOptions,
    offerType: OfferType,
    { logRefusals = false }: { logRefusals?: boolean } = {}
): IPenciledInputs => {
    const { term, rate, trade, downPayment, sellingPrice, buyRate, tradeIns, balloonAmount } = overrides;

    const termMonth = term.value;
    const effectiveRateType = declaredRateTypeForTerm(penciledInputs, offerType, termMonth);
    const writeSellRate = isWritableRate('rollToSellRate', effectiveRateType, rate, offerType, logRefusals);
    const writeBuyRate = isWritableRate('rollToBuyRate', effectiveRateType, buyRate, offerType, logRefusals);
    const doOverride = writeSellRate || writeBuyRate || term.adjusted;

    const isTradeArray = Array.isArray(tradeIns);
    const hasAdjustedTrade = isTradeArray ? tradeIns.some((trade) => trade.adjusted) : trade.adjusted;

    let tradeInsOverride;
    if (hasAdjustedTrade) {
        if (isTradeArray) {
            const existingTradeIns = penciledInputs?.byOfferType?.[offerType]?.tradeIns || [];

            const adjustedTradeIns = tradeIns
                .filter((trade) => trade?.adjusted)
                .map((trade) => {
                    const existingTradeIn = existingTradeIns.find((existing) => existing.callerItemId === trade.callerItemId);

                    return {
                        ...existingTradeIn,
                        value: trade.value,
                        callerItemId: trade.callerItemId
                    };
                });

            if (adjustedTradeIns.length > 0) {
                tradeInsOverride = { tradeIns: adjustedTradeIns };
            }
        } else {
            tradeInsOverride = {
                tradeIn: {
                    ...penciledInputs?.byOfferType?.[offerType]?.tradeIn,
                    value: trade.value
                }
            };
        }
    }
    if (!penciledInputs.byOfferType) return penciledInputs;
    return {
        ...penciledInputs,
        ...{
            byOfferType: {
                ...penciledInputs?.byOfferType,
                [offerType]: {
                    ...penciledInputs?.byOfferType?.[offerType],
                    isUpdatedByUser: true,
                    ...(writeSellRate && { sellRateOverride: rate.value }),
                    ...(writeBuyRate && { buyRateOverride: buyRate?.value }),
                    ...(downPayment.adjusted && { downPayment: downPayment.value }),
                    ...(balloonAmount?.adjusted && { balloonPaymentAmountOverride: balloonAmount.value }),
                    ...(sellingPrice.adjusted && { offerPrice: sellingPrice.value }),
                    ...tradeInsOverride,
                    ...(doOverride && {
                        overridesByTerm: {
                            ...penciledInputs?.byOfferType?.[offerType]?.overridesByTerm,
                            [term.value!]: {
                                ...carriedTermOverride(penciledInputs, offerType, termMonth, effectiveRateType, writeSellRate),
                                ...(writeSellRate && { sellRate: rate.value }),
                                ...(writeBuyRate && { buyRate: buyRate?.value })
                            }
                        }
                    })
                }
            }
        }
    };
};
